/**
 * Recommendations Card Component
 * Displays actionable security recommendations
 */
import React from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Alert,
  Paper,
  Button
} from '@mui/material';
import {
  Lightbulb as LightbulbIcon,
  PriorityHigh as HighPriorityIcon,
  Error as MediumPriorityIcon,
  Info as LowPriorityIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';

function RecommendationsCard({ recommendationsData, loading }) {
  if (!recommendationsData) {
    return (
      <Alert severity="info">
        No recommendations available. Run analytics to generate recommendations.
      </Alert>
    );
  }

  const getPriorityIcon = (priority) => {
    switch (priority.toLowerCase()) {
      case 'high':
        return <HighPriorityIcon color="error" />;
      case 'medium':
        return <MediumPriorityIcon color="warning" />;
      default:
        return <LowPriorityIcon color="info" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority.toLowerCase()) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      default:
        return 'info';
    }
  };

  const handleTakeAction = (recommendation) => {
    // TODO: Implement action handling
    console.log('Taking action on recommendation:', recommendation);
    alert('Action implementation coming soon!');
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Security Recommendations
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Actionable recommendations based on risk scores, anomalies, and security best practices.
        </Typography>
      </Box>

      {/* Summary Stats */}
      <Box sx={{ mb: 3, display: 'flex', gap: 3 }}>
        <Box>
          <Typography variant="body2" color="text.secondary">
            Recommendations
          </Typography>
          <Typography variant="h5">
            {recommendationsData.recommendations_generated}
          </Typography>
        </Box>
        <Box>
          <Typography variant="body2" color="text.secondary">
            High Priority
          </Typography>
          <Typography variant="h5" color="error.main">
            {recommendationsData.recommendations?.filter(r => r.priority === 'high').length || 0}
          </Typography>
        </Box>
        <Box>
          <Typography variant="body2" color="text.secondary">
            Medium Priority
          </Typography>
          <Typography variant="h5" color="warning.main">
            {recommendationsData.recommendations?.filter(r => r.priority === 'medium').length || 0}
          </Typography>
        </Box>
      </Box>

      {/* Recommendations List */}
      {recommendationsData.recommendations && recommendationsData.recommendations.length > 0 ? (
        <>
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Recommended Actions
          </Typography>
          <List>
            {recommendationsData.recommendations.map((recommendation, index) => (
              <Paper key={index} sx={{ mb: 2 }}>
                <ListItem>
                  <ListItemIcon>
                    {getPriorityIcon(recommendation.priority)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <LightbulbIcon color="primary" fontSize="small" />
                        <Typography variant="subtitle1" fontWeight="bold">
                          {recommendation.action}
                        </Typography>
                        <Chip
                          label={recommendation.priority.toUpperCase()}
                          color={getPriorityColor(recommendation.priority)}
                          size="small"
                        />
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.primary" sx={{ mb: 0.5 }}>
                          <strong>User:</strong> {recommendation.user_name || recommendation.user_email || recommendation.user_id}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          <strong>Reason:</strong> {recommendation.reason}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Type: {recommendation.type.replace('_', ' ')} | Generated: {new Date(recommendation.generated_at).toLocaleString()}
                        </Typography>
                        <Box sx={{ mt: 2 }}>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<CheckIcon />}
                            onClick={() => handleTakeAction(recommendation)}
                          >
                            Take Action
                          </Button>
                        </Box>
                      </Box>
                    }
                  />
                </ListItem>
              </Paper>
            ))}
          </List>
        </>
      ) : (
        <Alert severity="success" sx={{ mt: 3 }}>
          <Typography variant="body1">
            No recommendations at this time. Your security posture looks good!
          </Typography>
        </Alert>
      )}
    </Box>
  );
}

export default RecommendationsCard;
